import {DetailedError, parseResponse} from 'hono/client';
import {err, ok} from 'framework';
import type {Page, Result} from 'framework';
import type {RpcClient} from '@/infrastructure/rpc/rpc-client';
import type {
  CreateFromCatalogInput,
  CreateManualProductInput,
  ListProductsInput,
  ProductRepository,
  SearchCatalogCandidatesInput,
  UpdateProductInput,
} from '../../application/ports/product.repository';
import type {CatalogCandidate} from '../../domain/catalog-candidate';
import type {NutritionLabelScanResult, Product, ProductId} from '../../domain/product';
import type {ProductError} from '../../domain/errors';
import {
  InvalidNutritionLabelImageError,
  InvalidProductInputError,
  NetworkError,
  ProductAlreadyExistsError,
  ProductNotEditableError,
  ProductNotFoundError,
  ServerError,
  UnauthorizedError,
} from '../../domain/errors';
import type {ResolveResult} from '../../domain/resolve-result';
import type {CatalogCandidateDtoToCandidateMapper} from '../mappers/catalog-candidate-dto-to-candidate.mapper';
import type {ProductDtoToProductMapper} from '../mappers/product-dto-to-product.mapper';
import type {ResolveDtoToResolveResultMapper} from '../mappers/resolve-dto-to-resolve-result.mapper';

/**
 * Which operation failed. Product writes and the nutrition-label scan share
 * status codes with different meanings: a 400 / 422 on the scan route means
 * the image was rejected, not that the product is invalid or read-only.
 */
type ErrorContext = 'product' | 'nutritionLabel';

const productStatusErrorMap: Readonly<Record<number, () => ProductError>> = {
  400: () => new InvalidProductInputError(),
  401: () => new UnauthorizedError(),
  404: () => new ProductNotFoundError(),
  409: () => new ProductAlreadyExistsError(),
  422: () => new ProductNotEditableError(),
};

const statusErrorMaps: Readonly<
  Record<ErrorContext, Readonly<Record<number, () => ProductError>>>
> = {
  product: productStatusErrorMap,
  nutritionLabel: {
    ...productStatusErrorMap,
    400: () => new InvalidNutritionLabelImageError(),
    422: () => new InvalidNutritionLabelImageError(),
  },
};

export class RpcProductRepository implements ProductRepository {
  constructor(
    private readonly rpcClient: RpcClient,
    private readonly productMapper: ProductDtoToProductMapper,
    private readonly resolveMapper: ResolveDtoToResolveResultMapper,
    private readonly catalogCandidateMapper: CatalogCandidateDtoToCandidateMapper,
  ) {}

  async list(input: ListProductsInput): Promise<Result<Page<Product>, ProductError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient.products.$get({
          query: {
            ...(input.cursor !== null && {cursor: input.cursor}),
            ...(input.q.length > 0 && {q: input.q}),
          },
        }),
      );

      // Narrow the HTTP envelope union; in-repo success responses are trusted.
      if ('error' in dto || dto.meta === undefined) {
        return err(new ServerError());
      }

      return ok({
        items: dto.data.map(item => this.productMapper.transform(item)),
        nextCursor: dto.meta.nextCursor,
        hasMore: dto.meta.hasMore,
      });
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async getById(productId: ProductId): Promise<Result<Product, ProductError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient.products[':productId'].$get({
          param: {productId},
        }),
      );
      return ok(this.productMapper.transform(dto));
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async resolve(barcode: string): Promise<Result<ResolveResult, ProductError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient.products.resolve[':barcode'].$get({
          param: {barcode},
        }),
      );
      return ok(this.resolveMapper.transform(dto));
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async searchCatalogCandidates(
    input: SearchCatalogCandidatesInput,
  ): Promise<Result<readonly CatalogCandidate[], ProductError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient.products['catalog-candidates'].$get({
          query: {
            q: input.q,
            brand: input.brand ?? undefined,
          },
        }),
      );
      if ('error' in dto) {
        return err(new ServerError());
      }
      return ok(dto.data.map(item => this.catalogCandidateMapper.transform(item)));
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async createFromCatalog(input: CreateFromCatalogInput): Promise<Result<Product, ProductError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient.products['from-catalog'].$post({
          json:
            'fdcId' in input
              ? {
                  fdcId: input.fdcId,
                  ...(input.label !== undefined && {label: input.label}),
                  ...(input.serving !== undefined && {serving: input.serving}),
                }
              : {
                  barcode: input.barcode,
                  ...(input.label !== undefined && {label: input.label}),
                  ...(input.serving !== undefined && {serving: input.serving}),
                },
        }),
      );
      return ok(this.productMapper.transform(dto));
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async createManual(input: CreateManualProductInput): Promise<Result<Product, ProductError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient.products.manual.$post({
          json: input,
        }),
      );
      return ok(this.productMapper.transform(dto));
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async update(input: UpdateProductInput): Promise<Result<Product, ProductError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient.products[':productId'].$patch({
          param: {productId: input.productId},
          json: {
            ...(input.name !== undefined && {name: input.name}),
            ...(input.brand !== undefined && {brand: input.brand}),
            ...(input.label !== undefined && {label: input.label}),
            ...(input.kcalPer100g !== undefined && {kcalPer100g: input.kcalPer100g}),
            ...(input.proteinPer100g !== undefined && {proteinPer100g: input.proteinPer100g}),
            ...(input.carbsPer100g !== undefined && {carbsPer100g: input.carbsPer100g}),
            ...(input.fatPer100g !== undefined && {fatPer100g: input.fatPer100g}),
            ...(input.serving !== undefined && {serving: input.serving}),
          },
        }),
      );
      return ok(this.productMapper.transform(dto));
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async delete(productId: ProductId): Promise<Result<void, ProductError>> {
    try {
      await parseResponse(
        this.rpcClient.products[':productId'].$delete({
          param: {productId},
        }),
      );
      return ok(undefined);
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async scanNutritionLabel(image: File): Promise<Result<NutritionLabelScanResult, ProductError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient.products['scan-nutrition-label'].$post({
          form: {image},
        }),
      );
      return ok({
        name: dto.name,
        brand: dto.brand,
        kcalPer100g: dto.kcalPer100g,
        proteinPer100g: dto.proteinPer100g,
        carbsPer100g: dto.carbsPer100g,
        fatPer100g: dto.fatPer100g,
        servingGrams: dto.servingGrams,
        notes: dto.notes,
      });
    } catch (cause) {
      return this.mapError(cause, 'nutritionLabel');
    }
  }

  private mapError(cause: unknown, context: ErrorContext = 'product'): Result<never, ProductError> {
    if (cause instanceof DetailedError) {
      return err(statusErrorMaps[context][cause.statusCode]?.() ?? new ServerError());
    }
    return err(new NetworkError());
  }
}
